import { useState, useEffect, useRef } from "react";
import { type User } from "firebase/auth";
import { signInWithGoogle } from "./auth";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700;800&family=Barlow+Condensed:wght@700;800;900&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg:       #060b18;
    --bg2:      #0a1020;
    --cyan:     #00c8ff;
    --purple:   #8b5cf6;
    --blue:     #2563eb;
    --blue-h:   #1d4ed8;
    --white:    #f0f4ff;
    --muted:    #4a5568;
    --border:   rgba(255,255,255,0.07);
  }

  body { background: var(--bg); }

  .root {
    min-height: 100vh;
    background: var(--bg);
    display: grid;
    grid-template-columns: 1fr 420px;
    font-family: 'Barlow', sans-serif;
    color: var(--white);
    position: relative;
    overflow: hidden;
  }

  .particle-canvas {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  .grad {
    background: linear-gradient(90deg, var(--cyan), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .left {
    padding: 64px 52px 64px 72px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  .brand { margin-bottom: 44px; animation: slideLeft 0.8s cubic-bezier(0.16,1,0.3,1) both; }

  .brand-name {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 96px;
    font-weight: 900;
    letter-spacing: -2px;
    line-height: 0.92;
    background: linear-gradient(90deg, #fff 40%, var(--cyan));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .brand-sub { font-size: 16px; font-weight: 600; color: rgba(240,244,255,0.55); letter-spacing: 4px; margin-top: 8px; text-transform: uppercase; }
  .brand-sub span {
    background: linear-gradient(90deg, var(--cyan), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 600;
  }

  .tagline {
    font-size: 28px;
    font-weight: 400;
    line-height: 1.5;
    color: rgba(240,244,255,0.88);
    max-width: 480px;
    margin-bottom: 48px;
    animation: slideLeft 0.8s 0.1s cubic-bezier(0.16,1,0.3,1) both;
  }

  .features { display: flex; flex-direction: column; gap: 12px; max-width: 460px; }

  .feature {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 18px;
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border);
    border-radius: 12px;
    transition: background 0.2s, border-color 0.2s, transform 0.22s;
    cursor: default;
  }

  .feature:hover { background: rgba(0,200,255,0.05); border-color: rgba(0,200,255,0.2); transform: translateX(5px); }
  .feature:nth-child(1) { animation: slideUp 0.6s 0.22s cubic-bezier(0.16,1,0.3,1) both; }
  .feature:nth-child(2) { animation: slideUp 0.6s 0.30s cubic-bezier(0.16,1,0.3,1) both; }
  .feature:nth-child(3) { animation: slideUp 0.6s 0.38s cubic-bezier(0.16,1,0.3,1) both; }

  .feat-icon {
    font-size: 17px;
    width: 36px; height: 36px;
    background: rgba(0,200,255,0.08);
    border: 1px solid rgba(0,200,255,0.15);
    border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .feat-title { font-size: 13px; font-weight: 600; color: var(--white); margin-bottom: 3px; }
  .feat-desc  { font-size: 12px; color: var(--muted); font-weight: 400; line-height: 1.5; }

  .right {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 52px 40px 16px;
    position: relative;
    z-index: 1;
  }

  .card {
    width: 100%;
    background: rgba(10,16,32,0.8);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 36px 32px;
    animation: slideRight 0.9s 0.1s cubic-bezier(0.16,1,0.3,1) both;
  }

  .card::before {
    content: '';
    display: block;
    height: 2px; width: 60px;
    background: linear-gradient(90deg, var(--cyan), var(--purple));
    border-radius: 2px;
    margin-bottom: 28px;
  }

  .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 32px; }

  .stat {
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 14px 10px;
    text-align: center;
  }

  .stat-val {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 22px; font-weight: 800;
    display: block; margin-bottom: 3px;
    background: linear-gradient(90deg, var(--cyan), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .stat-lbl { font-size: 10px; color: var(--muted); font-weight: 500; text-transform: uppercase; letter-spacing: 1.5px; }

  .card-title { font-size: 20px; font-weight: 700; color: var(--white); margin-bottom: 8px; }
  .card-sub   { font-size: 13px; color: var(--muted); margin-bottom: 28px; font-weight: 400; line-height: 1.65; }

  .google-btn {
    width: 100%;
    padding: 13px 20px;
    background: var(--blue);
    border: none; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; gap: 12px;
    cursor: pointer;
    font-family: 'Barlow', sans-serif;
    font-size: 14px; font-weight: 600;
    color: #fff; letter-spacing: 0.3px;
    transition: background 0.18s, transform 0.18s, box-shadow 0.18s;
    margin-bottom: 18px;
    text-transform: uppercase;
  }

  .google-btn:hover { background: var(--blue-h); transform: translateY(-2px); box-shadow: 0 8px 28px rgba(37,99,235,0.35); }
  .google-btn:active { transform: translateY(0); }
  .google-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  .error-msg {
    font-size: 12px; color: #f87171; text-align: center;
    margin-bottom: 12px; padding: 8px 12px;
    background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.2);
    border-radius: 8px;
  }

  .terms { font-size: 11px; color: rgba(74,85,104,0.8); text-align: center; line-height: 1.65; }

  .court-svg { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 0; }

  @keyframes slideLeft  { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes slideRight { from { opacity: 0; transform: translateX(30px);  } to { opacity: 1; transform: translateX(0); } }
  @keyframes slideUp    { from { opacity: 0; transform: translateY(18px);  } to { opacity: 1; transform: translateY(0); } }
  @keyframes pulseDot   { 0%, 100% { opacity: 0.25; transform: scale(1);   } 50% { opacity: 0.65; transform: scale(1.6); } }
  @keyframes pulseRing  { 0%, 100% { opacity: 0.1; } 50% { opacity: 0.3; } }

  .s-dot  { animation: pulseDot  2.8s 2.4s ease-in-out infinite; transform-origin: 652px 658px; }
  .s-ring { animation: pulseRing 2.8s 2.4s ease-in-out infinite; }
`;

const FEATURES = [
  { icon: "⚡", title: "AI Pose Analysis",    desc: "33-point skeleton overlay tracks every joint angle & movement in real time" },
  { icon: "👻", title: "Ghost Pose & Angles", desc: "Replay your ideal form ghost, wrist trail, and live joint angle overlays" },
  { icon: "📊", title: "Progress Reports",    desc: "Session comparison, all-time bests, and downloadable PDF with AI coaching tips" },
];

const STATS = [
  { v: "33",    l: "Joints"   },
  { v: "60fps", l: "Analysis" },
  { v: "8+",    l: "Metrics"  },
  { v: "PDF",   l: "Reports"  },
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#fff" d="M43.6 20H24v8h11.3c-1.1 5.1-5.6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c2.9 0 5.6 1 7.7 2.8l5.7-5.7C33.7 5.8 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.3-7.7 19.3-20 0-1.3-.1-2.7-.3-4z" opacity="0.9"/>
  </svg>
);

function ParticleCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    type Dot = { x:number; y:number; r:number; a:number; s:number; d:number; t:number };
    const dots: Dot[] = Array.from({ length: 80 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.5 + 0.4,
      a: Math.random() * 0.6 + 0.1,
      s: (Math.random() - 0.5) * 0.15,
      d: Math.random() * 0.004 + 0.001,
      t: Math.random() * Math.PI * 2,
    }));

    let raf: number;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      dots.forEach(dot => {
        dot.t += dot.d; dot.y += dot.s;
        if (dot.y < -5) dot.y = H + 5;
        if (dot.y > H + 5) dot.y = -5;
        const alpha = dot.a * (0.6 + 0.4 * Math.sin(dot.t));
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 200, 255, ${alpha})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} className="particle-canvas" />;
}

export default function LoginPage({ onLogin }: { onLogin: (user: User) => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      onLogin(user);
    } catch (err: any) {
      setError(err?.message ?? "Sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <style>{css}</style>
      <div className="root">

        <ParticleCanvas />

        <svg className="court-svg" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="cg" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#00c8ff" stopOpacity="0.25"/>
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
          <rect x="55" y="80" width="625" height="640" fill="none" stroke="url(#cg)" strokeWidth="1.5" opacity="0.18"/>
          <line x1="55"  y1="400" x2="680" y2="400" stroke="#00c8ff" strokeWidth="1"   opacity="0.07"/>
          <line x1="368" y1="80"  x2="368" y2="720" stroke="#00c8ff" strokeWidth="1.8" opacity="0.1"/>
          <line x1="55"  y1="235" x2="680" y2="235" stroke="#00c8ff" strokeWidth="0.8" opacity="0.055"/>
          <line x1="55"  y1="565" x2="680" y2="565" stroke="#00c8ff" strokeWidth="0.8" opacity="0.055"/>
          <line x1="193" y1="235" x2="193" y2="565" stroke="#8b5cf6" strokeWidth="0.7" opacity="0.04"/>
          <line x1="543" y1="235" x2="543" y2="565" stroke="#8b5cf6" strokeWidth="0.7" opacity="0.04"/>
          <path d="M 90 700 Q 368 50 652 658" fill="none" stroke="url(#cg)" strokeWidth="2" strokeDasharray="9 5" opacity="0.22"/>
          <path d="M 140 655 Q 375 110 608 612" fill="none" stroke="#00c8ff" strokeWidth="0.9" strokeDasharray="6 5" opacity="0.08"/>
          <circle cx="652" cy="658" r="5"  fill="#00c8ff" opacity="0.35" className="s-dot"/>
          <circle cx="652" cy="658" r="12" fill="none" stroke="#00c8ff" strokeWidth="1" opacity="0.14" className="s-ring"/>
          <circle cx="295" cy="272" r="20" fill="none" stroke="#00c8ff" strokeWidth="1.5" opacity="0.12"/>
          <line x1="295" y1="292" x2="295" y2="388" stroke="#00c8ff" strokeWidth="1.5" opacity="0.1"/>
          <line x1="295" y1="328" x2="234" y2="312" stroke="#00c8ff" strokeWidth="1.5" opacity="0.1"/>
          <line x1="234" y1="312" x2="188" y2="280" stroke="#00c8ff" strokeWidth="1.5" opacity="0.12"/>
          <line x1="295" y1="328" x2="352" y2="348" stroke="#8b5cf6" strokeWidth="1.5" opacity="0.08"/>
          <line x1="295" y1="388" x2="263" y2="490" stroke="#00c8ff" strokeWidth="1.5" opacity="0.09"/>
          <line x1="295" y1="388" x2="327" y2="490" stroke="#00c8ff" strokeWidth="1.5" opacity="0.09"/>
          <circle cx="234" cy="312" r="4" fill="#00c8ff" opacity="0.2"/>
          <circle cx="188" cy="280" r="4" fill="#00c8ff" opacity="0.2"/>
          <circle cx="352" cy="348" r="4" fill="#8b5cf6" opacity="0.18"/>
          <circle cx="263" cy="490" r="4" fill="#00c8ff" opacity="0.15"/>
          <circle cx="327" cy="490" r="4" fill="#00c8ff" opacity="0.15"/>
          <path d="M 222 300 A 16 16 0 0 1 242 326" fill="none" stroke="#00c8ff" strokeWidth="1" opacity="0.2"/>
          <text x="198" y="318" fill="#00c8ff" fontSize="11" opacity="0.28" fontFamily="monospace">142°</text>
          <path d="M 220 342 Q 202 312 188 280" fill="none" stroke="#8b5cf6" strokeWidth="1" strokeDasharray="3 3" opacity="0.14"/>
        </svg>

        <div className="left">
          <div className="brand">
            <div className="brand-name">SHUTTLECOCK</div>
            <div className="brand-sub">by <span>statvo</span></div>
          </div>
          <p className="tagline">
            Turn footage into{" "}
            <span className="grad">precision insights</span>.<br />
            Every swing. Every jump. Every rally.
          </p>
          <div className="features">
            {FEATURES.map((f) => (
              <div className="feature" key={f.title}>
                <div className="feat-icon">{f.icon}</div>
                <div>
                  <div className="feat-title">{f.title}</div>
                  <div className="feat-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="right">
          <div className="card">
            <div className="stats-grid">
              {STATS.map((s) => (
                <div className="stat" key={s.l}>
                  <span className="stat-val">{s.v}</span>
                  <span className="stat-lbl">{s.l}</span>
                </div>
              ))}
            </div>

            <div className="card-title">Welcome back</div>
            <p className="card-sub">
              Sign in with Google to access your dashboard and track your progress over time.
            </p>

            {error && <div className="error-msg">{error}</div>}

            <button
              className="google-btn"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <GoogleIcon />
              {loading ? "Signing in…" : "Continue with Google"}
            </button>

            <p className="terms">
              Your session data is stored locally on your device only.
            </p>
          </div>
        </div>

      </div>
    </>
  );
}